import { apiClient } from "@/api/apiClient";
import {
	AnalysisClientError,
	type DesignDocumentAnalysisClient,
	type PresignedUploadTarget,
} from "@earthbrain/foundation-excavation-sdk";

/** Upload timeout (60s). Longer than the others because a document image can be several MB. */
const UPLOAD_TIMEOUT_MS = 60_000;

/**
 * Timeout for issuing the presigned URL, creating the job and fetching the
 * result (10s).
 *
 * Note these are not DB-only calls: v2-server checks site access by calling the
 * Platform API each time (`siteAccessMiddleware`). Even so, over 10s is abnormal.
 * The SDK polls for the result every 3s, so waiting longer would let calls pile up.
 */
const REQUEST_TIMEOUT_MS = 10_000;

/** Request options carrying the timeout, passed to the hono client's init. */
function withTimeout(timeoutMs: number): { init: RequestInit } {
	return { init: { signal: AbortSignal.timeout(timeoutMs) } };
}

/** Implementation of DesignDocumentAnalysisClient, providing the methods the SDK calls in turn. */
export function createDesignDocumentAnalysisClient(
	siteId: string,
): DesignDocumentAnalysisClient {
	return {
		async getPresignedUrl(fileName, contentType) {
			const res = await apiClient.v2.api[":siteId"][
				"foundation-excavation"
			].documents.$post(
				{
					param: { siteId },
					json: { fileName, contentType },
				},
				withTimeout(REQUEST_TIMEOUT_MS),
			);
			if (!res.ok) {
				throw new AnalysisClientError("署名付きURLの取得に失敗しました", {
					httpStatus: res.status,
				});
			}
			return res.json() as Promise<PresignedUploadTarget>;
		},

		async uploadFile(presignedUrl, file) {
			// fetch surfaces an abort (timeout) as a thrown error, so treat it as the
			// same kind of failure.
			let res: Response;
			try {
				res = await fetch(presignedUrl, {
					method: "PUT",
					headers: { "Content-Type": file.type },
					body: file,
					signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
				});
			} catch (cause: unknown) {
				throw new AnalysisClientError("ファイルのアップロードに失敗しました", {
					cause,
				});
			}
			if (!res.ok) {
				throw new AnalysisClientError("ファイルのアップロードに失敗しました", {
					httpStatus: res.status,
				});
			}
		},

		async createJob(documentId) {
			const res = await apiClient.v2.api[":siteId"][
				"foundation-excavation"
			].analysis.jobs.$post(
				{
					param: { siteId },
					json: { documentId, type: "grid" },
				},
				withTimeout(REQUEST_TIMEOUT_MS),
			);
			if (!res.ok) {
				throw new AnalysisClientError("解析ジョブの発行に失敗しました", {
					httpStatus: res.status,
				});
			}
			return res.json() as Promise<{ jobId: string }>;
		},

		async getResult(jobId) {
			const res = await apiClient.v2.api[":siteId"][
				"foundation-excavation"
			].analysis.results[":jobId"].$get(
				{
					param: { siteId, jobId },
				},
				withTimeout(REQUEST_TIMEOUT_MS),
			);
			if (!res.ok) {
				throw new AnalysisClientError("解析結果の取得に失敗しました", {
					httpStatus: res.status,
				});
			}
			return res.json();
		},
	};
}
